import React, { useContext, useMemo, useRef, useState } from "react";

import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AppContext } from "../../../../App";
import { Dialog, DialogContent, DialogTitle, Grid, IconButton } from "@material-ui/core";

import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import SearchIcon from "@material-ui/icons/Search";
import Table from "../../../../components/grade/Table";
import TablePagination from "../../../../components/grade/TablePagination";
import ApiChartAccounts from "../../../../api/accounting/chartAccounts";

export default function InputLookupChartAccounts({ label, value, onChangeValue, required, disabled }) {
    const { showMessageError } = useContext(AppContext);

    const [openModal, setOpenModal] = useState(false);

    const [rowCount, setRowCount] = useState(0);
    const [rowPage, setRowPage] = useState(5);
    const [page, setPage] = useState(0);
    const [dataSourceQuery, setDataSourceQuery] = useState([]);

    const [filterName, setFilterName] = useState("");

    const [open, setOpen] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const mayLoadDataSource = useRef(false);

    const loadDataSource = async () => {
        setLoading(true);

        // Condition
        const condition = {};
        if (inputValue) {
            condition["name_like"] = encodeURIComponent(inputValue + "+");
        }

        try {
            const result = await ApiChartAccounts().query({ page: 1, pageSize: 5, condition });

            if (result.status) {
                if (result.data.length === 1) {
                    setDataSource(result.data);
                    onChangeValue(result.data[0]);
                } else {
                    setDataSource(result.data);
                    setOpen(true);
                }
            } else {
                setDataSource([]);
            }
        } catch (e) {
            showMessageError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const metaData = useMemo(() => [
        {
            label: "Selecionar",
            render: (record) => {
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            onChangeValue({ ...record });
                            setOpenModal(false);
                        }}
                    >
                        <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                );
            },
        },
        {
            label: "Código",
            field: "id",
        },
        {
            label: "Nome",
            field: "name",
        },
    ]);

    const loadDataSourceQuery = async (newPage, newRowPage) => {
        // Condition
        const condition = {};
        if (filterName) {
            condition["name_like"] = encodeURIComponent(filterName + "+");
        }

        try {
            const result = await ApiChartAccounts().query({ page: newPage + 1, pageSize: newRowPage || rowPage, condition });

            if (result.status) {
                setDataSourceQuery(result.data);
                setRowCount(result.info.total);
            } else {
                setDataSource([]);
                if (result.message) {
                    showMessageError(result.message);
                }
            }
        } catch (e) {
            showMessageError(e.message);
        }
    };    

    return (
        <>
            <Autocomplete
                autoHighlight
                fullWidth
                disabled={disabled}
                noOptionsText="Digite o nome e pressione 'Enter'"
                forcePopupIcon={false}
                open={open}
                onClose={() => {
                    setOpen(false);
                }}
                getOptionSelected={(option, value) => option.id === value.id}
                getOptionLabel={(option) => {
                    if (!option?.name) {
                        return null;
                    }
                    return `${option.name} - ${option.id}`;
                }}
                renderOption={(option) => (
                    <>
                        {option.name} <br />
                        Código: {option.id}
                    </>
                )}
                options={dataSource}
                loading={loading}
                value={value}
                onChange={(event, value, reason) => {
                    onChangeValue(value);
                    if (reason === "select-option") {
                        mayLoadDataSource.current = false;
                    }
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                filterOptions={(options, state) => {
                    return options;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required={required}
                        label={label}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : (
                                        <IconButton size="small" onClick={() => setOpenModal(true)} disabled={disabled}>
                                            <SearchIcon fontSize="inherit" />
                                        </IconButton>
                                    )}
                                </React.Fragment>
                            ),
                        }}
                        onKeyDown={(event) => {
                            if (event.keyCode === 13) {
                                if (mayLoadDataSource.current) {
                                    loadDataSource();
                                }
                                mayLoadDataSource.current = false;
                            } else {
                                if (![9, 37, 38, 39, 40].includes(event.keyCode)) {
                                    mayLoadDataSource.current = true;
                                }
                            }
                        }}
                    />
                )}
            />
            <Dialog fullWidth maxWidth={"md"} open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle>{label}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                size="small"
                                label="Nome do plano de contas"
                                autoFocus
                                fullWidth
                                variant="outlined"
                                value={filterName}
                                onChange={(e) => setFilterName(e.target.value)}
                                onKeyDown={(event) => {
                                    if (event.keyCode === 13 && openModal) {
                                        loadDataSourceQuery(0);
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton size="small" onClick={() => loadDataSourceQuery(0)}>
                                            <SearchIcon fontSize="inherit" />
                                        </IconButton>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Table dataSource={dataSourceQuery} metaData={metaData} />
                    <TablePagination
                        rowCount={rowCount}
                        rowPage={rowPage}
                        page={page}
                        onPageChange={(event, newPage) => {
                            loadDataSourceQuery(newPage);
                            setPage(newPage);
                        }}
                        onRowsPerPageChange={(event) => {
                            loadDataSourceQuery(0, event.target.value);
                            setPage(0);
                            setRowPage(event.target.value);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
